import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUpIcon } from '@heroicons/react/24/outline';
import { useAppStore } from '@/stores/appStore';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';

const LEGEND_INLINE_LIMIT = 4;

/**
 * Bottom-of-view legend for initiative-record views (Timeline, List, Kanban, Batch
 * Update, Scoring, Dependency Log): classification colors + labels, plus a
 * dependencies-icon reminder when that Lab is on. Renders nothing if both the
 * classification field and the dependencies Lab are hidden/off.
 */
export function ClassificationFooter() {
  const classifications = useAppStore((s) => s.classifications);
  const fc = useFieldConfig();
  const labs = useLabsFeatures();
  const [legendOpen, setLegendOpen] = useState(false);
  const legendRef = useRef<HTMLDivElement>(null);

  const showClassification = fc.isVisible('classification');
  const showDependencies = labs.isEnabled('dependencies');

  const sortedClassifications = useMemo(
    () => [...classifications].sort((a, b) => a.badgeLabel.localeCompare(b.badgeLabel)),
    [classifications]
  );

  useEffect(() => {
    if (!legendOpen) return;
    const handler = (e: MouseEvent) => {
      if (legendRef.current && !legendRef.current.contains(e.target as Node)) {
        setLegendOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [legendOpen]);

  if (!showClassification && !showDependencies) return null;

  return (
    <div className="bg-background-secondary border-t border-gray-200 p-4 flex-shrink-0">
      <div className="flex items-center gap-6 text-sm">
        {showClassification && (
          <>
            <span className="font-medium text-text-secondary">
              {fc.label('classification').toUpperCase()}:
            </span>
            {sortedClassifications.length <= LEGEND_INLINE_LIMIT ? (
              sortedClassifications.map((c) => (
                <div key={c.id} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: c.color }} />
                  <span className="text-text-secondary">{c.badgeLabel}</span>
                </div>
              ))
            ) : (
              <div ref={legendRef} className="relative">
                <button
                  type="button"
                  onClick={() => setLegendOpen((o) => !o)}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-border-default rounded-md hover:bg-surface-hover text-text-secondary transition-colors"
                >
                  {sortedClassifications.length} types
                  <ChevronUpIcon className={`w-3 h-3 transition-transform ${legendOpen ? '' : 'rotate-180'}`} />
                </button>
                {legendOpen && (
                  <div className="absolute bottom-full mb-2 left-0 bg-background-elevated border border-border-default rounded-md shadow-lg p-3 space-y-2 min-w-[160px] z-50">
                    {sortedClassifications.map((c) => (
                      <div key={c.id} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: c.color }} />
                        <span className="text-text-secondary text-sm">{c.badgeLabel}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {showDependencies && (
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            <span className="text-text-secondary">{fc.label('dependencies').toUpperCase()}</span>
          </div>
        )}
      </div>
    </div>
  );
}
